import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import "react-datepicker/dist/react-datepicker.css";
import { Trans, useTranslation } from "react-i18next";
import { IoCloseSharp } from "react-icons/io5";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import CustomDatePicker from "../../../component/CustomDatePicker";
import CreateModal from "../../../component/Model/CreateModel";
import {
  useCreateDriverScheduleMutation,
  useGetScheduleDetailsQuery,
  useUpdateDriverScheduleMutation,
} from "../../../store/api/vendor/driversSlice";
import {
  capitalizeString,
  formatDateToYMD,
  handleApiError,
} from "../../../utils/dateAndTimeConversion";
import AddScheduleSkelton from "../../../component/skelton/AddScheduleSkelton";
import { parseLocalDate } from "../../../utils";

/* =========================
   CONSTANTS
========================= */

const initialState = {
  start_date: null,
  shift_type: "",
  start_time: "",
  end_time: "",
  return_start_time: "",
  return_end_time: "",
  recurrence_type: "",
  recurrence_days: [],
  end_date: null,
  notes: "",
};

const ALL_DAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const WEEKLY_DAYS = ALL_DAYS.slice(0, 7); // Mon → Sat
const BIWEEKLY_DAYS = ["monday", "thursday"];

export const getTomorrow = () => {
  const date = new Date();
  date.setDate(date.getDate()); // +1
  date.setHours(0, 0, 0, 0);
  return date;
};

/* =========================
   COMPONENT
========================= */

const AddSchedule = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const { id, sid } = useParams();
  const tomorrow = getTomorrow();
  const [show, setShow] = useState(false);
  const [overrideConflict, setOverrideConflict] = useState(null);

  // Create DRIVER SCHEDULE API
  const [createDriverSchedule, { isLoading }] =
    useCreateDriverScheduleMutation();

  const [updateDriverSchedule, { isLoading: isUpdateLoading }] =
    useUpdateDriverScheduleMutation();

  const {
    data,
    isSuccess,
    isLoading: isScheduleDetailLoading,
  } = useGetScheduleDetailsQuery(sid, { skip: !sid });

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "recurrence_type") {
      let days = [];

      if (value === "daily") days = ALL_DAYS;
      else if (value === "biweekly") days = BIWEEKLY_DAYS;
      else if (value === "weekly") days = [];
      else if (value === "monthly") days = [];

      setFormData((prev) => ({
        ...prev,
        recurrence_type: value,
        recurrence_days: days,
      }));

      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateChange = (date, fieldName) => {
    setFormData((prev) => {
      if (fieldName === "start_date" && prev.end_date && date > prev.end_date) {
        return {
          ...prev,
          start_date: date,
          end_date: null,
        };
      }

      return {
        ...prev,
        [fieldName]: date,
      };
    });
  };

  const handleDayChange = (day) => {
    setFormData((prev) => ({
      ...prev,
      recurrence_days: prev.recurrence_days.includes(day)
        ? prev.recurrence_days.filter((d) => d !== day)
        : [...prev.recurrence_days, day],
    }));
  };

  const isMorningAfternoon = formData.shift_type === "morning_afternoon";

  const validateForm = () => {
    const newErrors = {};

    if (!formData.start_date) {
      newErrors.start_date = t("start_date_required");
    }

    if (!formData.shift_type) {
      newErrors.shift_type = t("shift_type_required");
    }

    if (!formData.recurrence_type) {
      newErrors.recurrence_type = t("recurrence_type_required");
    }

    if (!formData.start_time) {
      newErrors.start_time = t("start_time_required");
    }

    if (!formData.end_time) {
      newErrors.end_time = t("end_time_required");
    }

    if (isMorningAfternoon) {
      if (!formData.return_start_time) {
        newErrors.return_start_time = t("afternoon_start_required");
      }
      if (!formData.return_end_time) {
        newErrors.return_end_time = t("afternoon_end_required");
      }
    }

    if (!formData.end_date) {
      newErrors.end_date = t("end_date_required");
    }

    if (
      formData.recurrence_type === "weekly" &&
      formData.recurrence_days.length === 0
    ) {
      newErrors.recurrence_days = t("select_at_least_one_day");
    }

    if (formData.start_date && formData.end_date) {
      if (formData.end_date < formData.start_date) {
        newErrors.end_date = t("end_date_after_start");
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildPayload = (forceOverride = false) => ({
    ...formData,
    start_date: formatDateToYMD(formData.start_date),
    end_date: formatDateToYMD(formData.end_date),
    ...(forceOverride && { force_override: true }),
  });

  const submitSchedule = async (forceOverride = false) => {
    const payload = buildPayload(forceOverride);
    try {
      let response;
      sid
        ? (response = await updateDriverSchedule({
            data: payload,
            sid,
          }).unwrap())
        : (response = await createDriverSchedule({
            data: payload,
            id,
          }).unwrap());

      if (response.statusCode === 200 || response.statusCode === 201) {
        setOverrideConflict(null);
        setShow(true);
      }
    } catch (error) {
      // 409 with conflict details → show override dialog instead of toast
      const data = error?.data;
      if (error?.status === 409 && data?.conflict) {
        setOverrideConflict({
          message: data.message,
          conflict: data.conflict,
        });
        return;
      }
      handleApiError(error, t);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    toast.dismiss();
    await submitSchedule(false);
  };

  const handleForceOverride = async () => {
    await submitSchedule(true);
  };

  const handleReset = () => {
    setFormData(initialState);
    setErrors({});
  };

  const handleClose = () => {
    setShow(false);
    navigate(`/drivers/${id}/schedules`);
  };

  // Day Button Component with your existing inline styles
  const DayButton = ({ day, isSelected, onClick, isDisabled = false }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={isDisabled}
      className="day-button"
      style={{
        padding: "10px 20px",
        border: isSelected ? "2px solid #f0b342" : "2px solid #dee2e6",
        borderRadius: "8px",
        backgroundColor: isSelected ? "#f0b342" : "white",
        color: isSelected ? "white" : "#495057",
        cursor: isDisabled ? "not-allowed" : "pointer",
        fontSize: "14px",
        fontWeight: "500",
        transition: "all 0.2s ease",
        opacity: isDisabled ? 0.6 : 1,
        minWidth: "100px",
        margin: "4px",
      }}
      onMouseEnter={(e) => {
        if (!isDisabled && !isSelected) {
          e.target.style.backgroundColor = "#f8f9fa";
          e.target.style.borderColor = "#adb5bd";
        }
      }}
      onMouseLeave={(e) => {
        if (!isDisabled && !isSelected) {
          e.target.style.backgroundColor = "white";
          e.target.style.borderColor = "#dee2e6";
        }
      }}
    >
      {capitalizeString(day)} {isSelected && "✓"}
    </button>
  );

  useEffect(() => {
    if (isSuccess && data.data) {
      const d = data.data;
      setFormData({
        start_date: parseLocalDate(d.start_date),
        end_date: parseLocalDate(d.end_date),
        shift_type: d.shift_type || "",
        start_time: d.start_time || "",
        end_time: d.end_time || "",
        return_start_time: d.return_start_time || "",
        return_end_time: d.return_end_time || "",
        recurrence_type: d.recurrence_type || "",
        recurrence_days: d.recurrence_days || [],
        notes: d.notes || "",
      });
    }
  }, [isSuccess, data]);

  return (
    <>
      <div className="outerBox">
        <div className="d-flex mb-3">
          <button className="page-back-btn" onClick={() => navigate(-1)}>
            ← {t("back")}
          </button>
        </div>
        <div className="dbSubHd mb-4">
          {t("schedule")} {t("details")}
        </div>
        {isScheduleDetailLoading ? (
          <AddScheduleSkelton />
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="row">
              {/* Start Date */}
              <div className="col-lg-4 mb-4">
                <CustomDatePicker
                  label={`${t("start")} ${t("date")}`}
                  selected={formData.start_date}
                  onChange={(date) => handleDateChange(date, "start_date")}
                  // minDate={tomorrow}
                  error={errors.start_date}
                  className="formInput"
                />
              </div>

              {/* End Date */}
              <div className="col-lg-4 mb-4">
                <CustomDatePicker
                  label={`${t("end")} ${t("date")}`}
                  selected={formData.end_date}
                  onChange={(date) => handleDateChange(date, "end_date")}
                  minDate={formData.start_date || tomorrow}
                  error={errors.end_date}
                  className="formInput"
                />
              </div>

              {/* Morning Start Time */}
              <div className="col-lg-4 mb-4">
                <label className="form-label fw-semibold">
                  {isMorningAfternoon ? `${t("morning")} ` : ""}
                  {`${t("start")} ${t("time")}`}{" "}
                  <span className="text-danger">*</span>
                </label>
                <input
                  type="time"
                  className={`formInput ${errors.start_time ? "error-border" : ""}`}
                  name="start_time"
                  value={formData.start_time}
                  onChange={handleChange}
                  style={{ width: "100%" }}
                />
                {errors?.start_time && (
                  <span className="text-danger small">{errors.start_time}</span>
                )}
              </div>

              {/* Morning End Time */}
              <div className="col-lg-4 mb-4">
                <label className="form-label fw-semibold">
                  {isMorningAfternoon ? `${t("morning")} ` : ""}
                  {`${t("end")} ${t("time")}`}{" "}
                  <span className="text-danger">*</span>
                </label>
                <input
                  type="time"
                  className={`formInput ${errors.end_time ? "error-border" : ""}`}
                  name="end_time"
                  value={formData.end_time}
                  onChange={handleChange}
                  style={{ width: "100%" }}
                />
                {errors.end_time && (
                  <span className="text-danger small">{errors.end_time}</span>
                )}
              </div>

              {/* Afternoon times — only for morning_afternoon */}
              {isMorningAfternoon && (
                <>
                  <div className="col-lg-4 mb-4">
                    <label className="form-label fw-semibold">
                      Afternoon {t("start")} {t("time")}{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="time"
                      className={`formInput ${errors.return_start_time ? "error-border" : ""}`}
                      name="return_start_time"
                      value={formData.return_start_time}
                      onChange={handleChange}
                      style={{ width: "100%" }}
                    />
                    {errors.return_start_time && (
                      <span className="text-danger small">
                        {errors.return_start_time}
                      </span>
                    )}
                  </div>
                  <div className="col-lg-4 mb-4">
                    <label className="form-label fw-semibold">
                      Afternoon {t("end")} {t("time")}{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="time"
                      className={`formInput ${errors.return_end_time ? "error-border" : ""}`}
                      name="return_end_time"
                      value={formData.return_end_time}
                      onChange={handleChange}
                      style={{ width: "100%" }}
                    />
                    {errors.return_end_time && (
                      <span className="text-danger small">
                        {errors.return_end_time}
                      </span>
                    )}
                  </div>
                </>
              )}

              {/* Shift Type */}
              <div className="col-lg-4 mb-4">
                <label className="form-label fw-semibold">
                  {t("shift")} {t("type")}{" "}
                  <span className="text-danger">*</span>
                </label>
                <select
                  className={`formSelect ${
                    errors.shift_type ? "error-border" : ""
                  }`}
                  name="shift_type"
                  value={formData.shift_type}
                  onChange={handleChange}
                  style={{ width: "100%" }}
                >
                  <option value="">
                    {t("select")} {t("shift")} {t("type")}
                  </option>
                  <option value="morning">{t("morning")}</option>
                  <option value="afternoon">{t("afternoon")}</option>
                  <option value="morning_afternoon">
                    {t("Morning & Afternoon")}
                  </option>
                  {/* <option value="evening">{t("evening")}</option> */}
                  {/* <option value="night">{t("night")}</option> */}
                </select>
                {errors.shift_type && (
                  <span className="text-danger small">{errors.shift_type}</span>
                )}
              </div>

              {/* Recurrence type */}
              <div className="col-lg-4 mb-4">
                <label className="form-label fw-semibold">
                  {t("recurrence")} {t("type")}{" "}
                  <span className="text-danger">*</span>
                </label>
                <select
                  className={`formSelect ${
                    errors.recurrence_type ? "error-border" : ""
                  }`}
                  name="recurrence_type"
                  value={formData.recurrence_type}
                  onChange={handleChange}
                  style={{ width: "100%" }}
                >
                  <option value="">
                    {t("select")} {t("recurrence")}
                  </option>
                  <option value="monthly">{t("monthly")}</option>
                  <option value="biweekly">{t("biweekly")}</option>
                  <option value="weekly">{t("weekly")}</option>
                  <option value="daily">{t("daily")}</option>
                </select>
                {errors.recurrence_type && (
                  <span className="text-danger small">
                    {errors.recurrence_type}
                  </span>
                )}
              </div>

              {/* Days Selection Section */}
              {formData.recurrence_type && (
                <div className="col-12 mb-4">
                  <div
                    className="schedule-section p-3 rounded"
                    style={{
                      backgroundColor: "#f8f9fa",
                      border: "1px solid #e9ecef",
                      borderRadius: "8px",
                    }}
                  >
                    <h6
                      className="section-title mb-3"
                      style={{
                        fontWeight: "600",
                        color: "#495057",
                        fontSize: "16px",
                      }}
                    >
                      {formData.recurrence_type === "daily" &&
                        "📅 Daily Schedule"}
                      {formData.recurrence_type === "weekly" &&
                        "📅 Weekly Schedule"}
                      {formData.recurrence_type === "biweekly" &&
                        "🔄 Bi-weekly Schedule"}
                      {formData.recurrence_type === "monthly" &&
                        "📆 Monthly Schedule"}
                    </h6>

                    {/* Daily */}
                    {formData.recurrence_type === "daily" && (
                      <div
                        className="info-message p-3 rounded"
                        style={{
                          backgroundColor: "#d1ecf1",
                          color: "#0c5460",
                          borderLeft: "4px solid #0c5460",
                        }}
                      >
                        <strong>
                          {t("daily")} {t("schedule")}:
                        </strong>{" "}
                        {t("schedule-repeat")}
                        <div className="mt-2 small">
                          {t("from")}{" "}
                          {formData.start_date
                            ? formData.start_date.toLocaleDateString()
                            : "start date"}{" "}
                          to{" "}
                          {formData.end_date
                            ? formData.end_date.toLocaleDateString()
                            : "end date"}
                        </div>
                      </div>
                    )}

                    {/* Weekly */}
                    {formData.recurrence_type === "weekly" && (
                      <>
                        <p className="text-muted mb-3 small">
                          {t("days-for-weekly-schedule")}:
                        </p>
                        <div className="days-container d-flex flex-wrap">
                          {WEEKLY_DAYS.map((day) => (
                            <DayButton
                              key={day}
                              day={day}
                              isSelected={formData.recurrence_days.includes(
                                day,
                              )}
                              onClick={() => handleDayChange(day)}
                            />
                          ))}
                        </div>
                        {errors.recurrence_days && (
                          <span className="text-danger small d-block mt-2">
                            {errors.recurrence_days}
                          </span>
                        )}
                        {formData.recurrence_days.length > 0 && (
                          <div
                            className="selected-days mt-3 p-2 rounded"
                            style={{
                              backgroundColor: "#d4edda",
                              color: "#155724",
                            }}
                          >
                            <small>
                              <strong>{t("selected")}:</strong>{" "}
                              {formData.recurrence_days
                                .map((d) => capitalizeString(d))
                                .join(", ")}
                            </small>
                          </div>
                        )}
                      </>
                    )}

                    {/* Biweekly */}
                    {formData.recurrence_type === "biweekly" && (
                      <div
                        className="info-message p-3 rounded"
                        style={{
                          backgroundColor: "#fff3cd",
                          color: "#856404",
                          borderLeft: "4px solid #856404",
                        }}
                      >
                        <strong>{t("bi-weekly-schedule")}:</strong>{" "}
                        {t("every-monday-and-thursday")}
                        <div className="mt-2 d-flex gap-2">
                          {BIWEEKLY_DAYS.map((day) => (
                            <span
                              key={day}
                              className="badge"
                              style={{
                                backgroundColor: "#f0b342",
                                color: "#1e293b",
                                padding: "6px 12px",
                                borderRadius: "20px",
                                fontSize: "12px",
                                fontWeight: "500",
                              }}
                            >
                              {capitalizeString(day)}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Monthly */}
                    {formData.recurrence_type === "monthly" && (
                      <div
                        className="info-message p-3 rounded"
                        style={{
                          backgroundColor: "#e2e3e5",
                          color: "#383d41",
                          borderLeft: "4px solid #383d41",
                        }}
                      >
                        <strong>{t("monthly-schedule")}:</strong>{" "}
                        {t("schedule-repeat-same-date-each-month")}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Notes */}
              <div className="col-12 mb-4">
                <label className="form-label fw-semibold">{t("Notes")}</label>
                <textarea
                  className="formInput"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows="4"
                  style={{ width: "100%" }}
                  placeholder={`${t("additional-notes")}...`}
                />
              </div>
            </div>

            {/* Buttons */}
            <div className="pt-4 mt-3 border-top text-end">
              <Button
                variant="outline-secondary"
                onClick={handleReset}
                className="me-3 px-4 py-2"
              >
                {t("btns.reset")}
              </Button>
              <Button
                variant="primary"
                type="button"
                onClick={handleSubmit}
                disabled={isLoading || isUpdateLoading}
                className="px-4 py-2"
              >
                {isLoading || isUpdateLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    >
                      <span className="visually-hidden">{t("loading")}...</span>
                    </span>
                  </>
                ) : (
                  t("btns.submit")
                )}
              </Button>
            </div>
          </form>
        )}
      </div>

      <Modal show={show} centered>
        <Button className="closePopup" onClick={handleClose}>
          <IoCloseSharp />
        </Button>
        <CreateModal
          onClose={handleClose}
          title={`${t("driver")} ${t("schedule")} ${
            id ? t("updated") : t("added")
          } ${t("successfully")}!`}
          text={
            id
              ? `${t("driver")} ${t("schedule")} ${t("has")} ${t("been")} ${t(
                  "updated",
                )}.`
              : `${t("driver")} ${t("schedule")} ${t("has")} ${t("been")} ${t(
                  "added",
                )}.`
          }
        />
      </Modal>

      {/* Schedule conflict override dialog */}
      <Modal
        show={!!overrideConflict}
        centered
        onHide={() => setOverrideConflict(null)}
      >
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="fs-5 fw-bold">
            {t("schedule_conflict_detected")}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p style={{ fontSize: "14px", color: "#555", marginBottom: "12px" }}>
            <Trans
              i18nKey="driver_schedule_conflict"
              values={{
                date: overrideConflict?.conflict?.instanceDate,
                reservationId: overrideConflict?.conflict?.reservationId,
              }}
              components={{ strong: <strong /> }}
            />
          </p>
          <div
            style={{
              background: "#fff8e1",
              border: "1px solid #f0b342",
              borderRadius: "8px",
              padding: "10px 14px",
              fontSize: "13px",
              color: "#795548",
            }}
          >
            <Trans
              i18nKey="force_override_note"
              components={{ strong: <strong /> }}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setOverrideConflict(null)}
          >
            {t("btns.close")}
          </Button>
          <Button
            variant="primary"
            onClick={handleForceOverride}
            disabled={isLoading}
            style={{ color: "#fff" }}
          >
            {isLoading ? (
              <span className="spinner-border spinner-border-sm me-1" />
            ) : (
              t("force_override")
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AddSchedule;
